import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export interface IdocStatus {
  configured: boolean;
  environment: 'dev' | 'prod';
  enabled: boolean;
  accounts_count: number;
  active_accounts_count: number;
  needs_relink_count: number;
  webhook_available: boolean;
  component_script_url: string;
}

export interface IdocAccount {
  id: string;
  unit_id: string | null;
  label: string;
  idoc_email: string | null;
  idoc_user_id: string;
  professional_id: string | null;
  professional_name: string | null;
  linked_by_user_id: string | null;
  linked_by_name: string | null;
  status: 'active' | 'needs_relink';
  last_error: string | null;
  webhook_status: 'pending' | 'confirmed' | 'failed' | 'unavailable' | null;
  webhook_error: string | null;
  last_polled_at: string | null;
  created_at: string;
}

export interface IdocDocItem {
  cod_item: string | null;
  name: string | null;
  type: string | null;
  thumbnail_url: string | null;
}

export interface IdocPatientDoc {
  cod_doc: string;
  account_id: string;
  account_label: string;
  patient_name: string | null;
  name_matches: boolean;
  birth_date: string | null;
  created_at: string | null;
  updated_at: string | null;
  is_new: boolean;
  items: IdocDocItem[];
}

export interface IdocAccountIssue {
  account_id: string;
  label: string;
  message: string;
}

export interface IdocPatientDocsResult {
  status: 'ok' | 'no_account' | 'missing_criteria' | 'error';
  docs: IdocPatientDoc[];
  errors: IdocAccountIssue[];
  criteria: { cpf: boolean; nameBirthSex: boolean; email: boolean };
  accounts_count: number;
  missing_for_create: string[];
}

export interface IdocViewerSession {
  account_id: string;
  account_label: string;
  patient_name: string | null;
  doc_id: string | null;
  session: Record<string, unknown>;
}

export interface IdocViewerSessionsResult {
  script_url: string;
  sessions: IdocViewerSession[];
}

export interface IdocInboxItem {
  id: string;
  account_id: string;
  cod_doc: string;
  patient_id: string | null;
  patient_name: string | null;
  idoc_patient_name: string | null;
  item_count: number;
  thumbnail_url: string | null;
  doc_updated_at: string | null;
  received_at: string;
  is_new: boolean;
  source: 'webhook' | 'poll';
}

export interface IdocInbox {
  items: IdocInboxItem[];
  unseen_count: number;
  errors: IdocAccountIssue[];
}

export class IdocApiError extends Error {
  constructor(message: string, public readonly code: string | null, public readonly status: number) {
    super(message);
  }
}

export function idocApiError(error: unknown): IdocApiError {
  const e = error as { response?: { status?: number; data?: { error?: string; code?: string } }; message?: string };
  return new IdocApiError(
    e?.response?.data?.error || e?.message || 'Erro na integração iDoc.',
    e?.response?.data?.code || null,
    e?.response?.status || 0,
  );
}

const base = '/clinic/idoc';

async function call<T>(request: Promise<{ data: { data: T } }>): Promise<T> {
  try {
    return (await request).data.data;
  } catch (error) {
    throw idocApiError(error);
  }
}

export const idocService = {
  getStatus: () => call<IdocStatus>(axios.get(getApiUrl(`${base}/status`))),
  listAccounts: () => call<IdocAccount[]>(axios.get(getApiUrl(`${base}/accounts`))),
  linkAccount: (payload: { email: string; password: string; label?: string; professional_id?: string | null }) =>
    call<IdocAccount>(axios.post(getApiUrl(`${base}/accounts`), payload)),
  unlinkAccount: (id: string) => call<void>(axios.delete(getApiUrl(`${base}/accounts/${id}`))),
  retryWebhook: (id: string) => call<IdocAccount>(axios.post(getApiUrl(`${base}/accounts/${id}/webhook`))),
  getInbox: (refresh = false) =>
    call<IdocInbox>(axios.get(getApiUrl(`${base}/inbox`), { params: refresh ? { refresh: 1 } : {} })),
  getPatientDocs: (patientId: string) =>
    call<IdocPatientDocsResult>(axios.get(getApiUrl(`${base}/patients/${patientId}/docs`))),
  getPatientUnseenCount: (patientId: string) =>
    call<{ count: number }>(axios.get(getApiUrl(`${base}/patients/${patientId}/unseen-count`))),
  startPatientViewer: (patientId: string) =>
    call<IdocViewerSessionsResult>(axios.post(getApiUrl(`${base}/patients/${patientId}/viewer-session`))),
  createPatient: (patientId: string, accountId?: string) =>
    call<{ already_existed: boolean; cod_doc: string | null; account_id: string }>(
      axios.post(getApiUrl(`${base}/patients/${patientId}/create`), accountId ? { account_id: accountId } : {}),
    ),
  getDocLink: (codDoc: string, accountId: string) =>
    call<{ url: string }>(axios.post(getApiUrl(`${base}/docs/${encodeURIComponent(codDoc)}/link`), { account_id: accountId })),
  startDocViewer: (codDoc: string, accountId: string) =>
    call<IdocViewerSessionsResult>(
      axios.post(getApiUrl(`${base}/docs/${encodeURIComponent(codDoc)}/viewer-session`), { account_id: accountId }),
    ),
};

/**
 * Abre o portal iDoc numa aba nova. A aba é aberta antes da chamada para não
 * ser bloqueada como pop-up (o link autenticado só chega depois do clique).
 */
export async function openIdocDocInPortal(codDoc: string, accountId: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const { url } = await idocService.getDocLink(codDoc, accountId);
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
}
